import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useGetSupplierContactsPreviewQuery, useUpdateSupplierContactsMutation } from "@/api/suppliers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { buildContactsRequest, toFormValues, type ContactsFormValues } from "./supplier-contacts-form";

interface Props {
  supplierId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const isStaleConflict = (error: unknown) =>
  (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code === "supplier_contacts_stale";

export const EditSupplierContactsDialog = ({ supplierId, open, onOpenChange }: Props) => {
  const { data, isLoading, isError, refetch } = useGetSupplierContactsPreviewQuery(
    { supplierId, params: { limit: 100 } },
    { skip: !open },
  );
  const [updateContacts, { isLoading: isSaving }] = useUpdateSupplierContactsMutation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setError,
  } = useForm<ContactsFormValues>();

  React.useEffect(() => {
    if (open && data) reset(toFormValues(data));
  }, [data, open, reset]);

  const values = watch();

  const submit = async (formValues: ContactsFormValues) => {
    if (!data) return;
    const request = buildContactsRequest(data, formValues);
    if ((request.family_changes?.length ?? 0) > 100) {
      setError("root.server", { message: "No more than 100 family changes can be saved at once." });
      return;
    }
    try {
      await updateContacts({ supplierId, data: request }).unwrap();
      toast.success("Contacts updated");
      onOpenChange(false);
    } catch (error) {
      setError("root.server", {
        message: isStaleConflict(error)
          ? "A Contract Family changed while this dialog was open. Review the refreshed contacts and try again."
          : "Contacts could not be updated.",
      });
      if (isStaleConflict(error)) refetch();
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !isSaving && onOpenChange(next)}>
      <DialogContent closeDisabled={isSaving} className="max-h-[90vh] sm:max-w-3xl">
        <form onSubmit={handleSubmit(submit)}>
          <DialogHeader>
            <DialogTitle>Edit contacts</DialogTitle>
            <DialogDescription>
              Supplier defaults are inherited dynamically unless an editable family uses a custom contact.
            </DialogDescription>
          </DialogHeader>
          {isLoading ? <p className="py-10 text-center text-sm text-slate-500">Loading contacts…</p> : null}
          {isError ? <p className="py-10 text-center text-sm text-red-700">Contacts could not be loaded.</p> : null}
          {data ? (
            <div className="mt-5 space-y-6">
              <section aria-labelledby="supplier-contact-heading">
                <h3 id="supplier-contact-heading" className="text-sm font-semibold text-slate-950">
                  Supplier default
                </h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-name">Contact name</Label>
                    <Input id="contact-name" maxLength={255} {...register("contact_name")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-email">Contact email</Label>
                    <Input id="contact-email" type="email" maxLength={320} {...register("contact_email")} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="contact-phone">Contact phone</Label>
                    <Input id="contact-phone" maxLength={64} {...register("contact_phone")} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="contact-notes">Notes</Label>
                    <Textarea id="contact-notes" rows={3} maxLength={4000} {...register("contact_notes")} />
                  </div>
                </div>
              </section>
              <section aria-labelledby="family-contact-heading">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 id="family-contact-heading" className="text-sm font-semibold text-slate-950">
                    Contract Family contacts
                  </h3>
                  <span className="text-xs text-slate-500">{data.total} accessible</span>
                </div>
                {data.total > data.families.length ? (
                  <p className="mt-2 text-xs text-amber-700">
                    Only the first {data.families.length} families can be edited in one save.
                  </p>
                ) : null}
                <div className="mt-3 space-y-3">
                  {data.families.map(family => {
                    const mode = values?.families?.[family.relation_id]?.mode ?? family.mode;
                    return (
                      <fieldset
                        key={family.relation_id}
                        className="rounded-lg border border-slate-200 p-3"
                        disabled={!family.can_edit}
                      >
                        <legend className="px-1 text-sm font-medium text-slate-900">{family.folder_name}</legend>
                        <div className="grid gap-3 sm:grid-cols-[180px_1fr_1fr]">
                          <div className="space-y-1.5">
                            <Label htmlFor={`contact-mode-${family.relation_id}`}>Contact source</Label>
                            <select
                              id={`contact-mode-${family.relation_id}`}
                              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                              {...register(`families.${family.relation_id}.mode`)}
                            >
                              <option value="inherited">Inherited</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                          {mode === "custom" ? (
                            <>
                              <div className="space-y-1.5">
                                <Label htmlFor={`family-name-${family.relation_id}`}>Contact name</Label>
                                <Input
                                  id={`family-name-${family.relation_id}`}
                                  maxLength={255}
                                  {...register(`families.${family.relation_id}.contact_name`)}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor={`family-email-${family.relation_id}`}>Contact email</Label>
                                <Input
                                  id={`family-email-${family.relation_id}`}
                                  type="email"
                                  maxLength={320}
                                  {...register(`families.${family.relation_id}.contact_email`)}
                                />
                              </div>
                              <div className="space-y-1.5 sm:col-span-2 sm:col-start-2">
                                <Label htmlFor={`family-phone-${family.relation_id}`}>Contact phone</Label>
                                <Input
                                  id={`family-phone-${family.relation_id}`}
                                  maxLength={64}
                                  {...register(`families.${family.relation_id}.contact_phone`)}
                                />
                              </div>
                            </>
                          ) : (
                            <p className="self-end pb-2 text-sm text-slate-500 sm:col-span-2">
                              Preview:{" "}
                              {[values?.contact_name, values?.contact_email, values?.contact_phone]
                                .filter(Boolean)
                                .join(" · ") || "No Supplier default set"}
                            </p>
                          )}
                        </div>
                        {!family.can_edit ? (
                          <p className="mt-2 text-xs text-slate-500">
                            Read-only: root document ownership is required to change this contact.
                          </p>
                        ) : null}
                      </fieldset>
                    );
                  })}
                </div>
              </section>
            </div>
          ) : null}
          {errors.root?.server ? (
            <p role="alert" className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {errors.root.server.message}
            </p>
          ) : null}
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!data || isSaving}>
              {isSaving ? "Saving…" : "Save contacts"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
